// Sessions + Session Detail (docs/screens/11-sessions.md).
//
// O dono central da tela é o transcript do runner, mas o transporte dele até
// o navegador é pergunta aberta (issue #8) — então tudo que é DERIVADO do
// transcript (log, contadores de mensagens/tool calls, Files touched) fica
// fora até esse transporte existir. Da tabela `sessions` do nosso estado saem
// papel, task, início/fim e a chave etiquetada; o nome canônico do agente e os
// tokens vêm do LiteLLM, que é quem o Contrato nomeia dono desses dois.

import { litellm, SourceError, state, type Agent, type Session, type SpendLog } from "../api";
import {
  duration, empty, errorBox, esc, head, pill, skeleton, table,
} from "../ui";

const CSS = `
.sessions-meta{display:flex;gap:14px;flex-wrap:wrap;font-size:12px;color:var(--muted,#8a8f98);margin:2px 0 14px}
.sessions-back{text-decoration:none;font-size:18px;line-height:1;margin-right:2px}
.sessions-chips{display:flex;gap:8px;margin-bottom:14px}
.sessions-ended{margin-top:16px;padding:10px 14px;border-radius:8px;background:rgba(128,128,128,.12);color:var(--muted,#8a8f98);font-size:13px;text-align:center}
`;
if (!document.getElementById("css-sessions")) {
  const st = document.createElement("style");
  st.id = "css-sessions";
  st.textContent = CSS;
  document.head.append(st);
}

/** Datas no formato do spec: "Aug 2, 3:42 PM". */
const quando = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

/** Tokens no formato do spec: "8.9K". */
const kTokens = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

/** `select("*")` já traz a chave etiquetada da sessão; o tipo público de
 *  api.ts ainda não a declara. */
type Sessao = Session & { litellm_key: string | null };

/** Nome canônico do agente, de `GET /v1/agents` (LiteLLM, Contrato). O que a
 *  sessão guarda é papel ("executor"), não nome — sem casamento, mostra o
 *  papel mesmo, que é o melhor que temos. */
function nomeAgente(agentes: Agent[], role: string): string {
  const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, "-");
  const a = agentes.find(
    (x) => norm(x.agent_name) === norm(role) || norm(x.agent_card_params?.name ?? "") === norm(role),
  );
  return a?.agent_card_params?.name ?? a?.agent_name ?? role;
}

/** Soma dos tokens de um log de chave. */
const somaTokens = (logs: SpendLog[]): number =>
  logs.reduce((n, l) => n + (l.total_tokens ?? 0), 0);

/** Tokens da sessão — `GET /spend/logs?api_key=`, o log cru de UMA chave.
 *
 *  Antes vinha de `/spend/logs` sem filtro: o log inteiro do gateway (14.452
 *  linhas, 81 MB) baixado para somar por chave. A chave é etiquetada por
 *  sessão, então o filtro por `api_key` devolve só as chamadas dessa sessão —
 *  dezenas de linhas — e a soma é a mesma. O agregado diário (`spendByDay`)
 *  não serve aqui: ele indexa por hash da chave, não pela chave, e o número
 *  desta tela é por sessão, não por dia.
 *
 *  A coluna `tokens` do nosso estado continua não sendo fonte: nós não
 *  contamos token. Best-effort: sem LiteLLM a tela abre, só sem esse número. */
async function tokensDaChave(apiKey: string): Promise<number | null> {
  try {
    return somaTokens(await litellm.spendForKey(apiKey)) || null;
  } catch {
    return null;
  }
}

/** As leituras da tela.
 *  ponytail: state.sessions() traz as 50 mais recentes; paginação quando doer. */
async function dados(): Promise<{ sessoes: Sessao[]; agentes: Agent[] }> {
  const [{ data: ss, error }, agentes] = await Promise.all([
    state.sessions(),
    litellm.agents().catch(() => [] as Agent[]),
  ]);
  if (error) throw new SourceError("Supabase", error.message, "confira a sessão e o projeto");

  return { sessoes: (ss ?? []) as Sessao[], agentes };
}

const btnRefresh = `<button class="btn" id="sessions-refresh">⟳ Refresh</button>`;

/** Reaplica o Refresh depois de cada render — ele só refaz as leituras. */
function ligaRefresh(el: HTMLElement): void {
  el.querySelector<HTMLButtonElement>("#sessions-refresh")?.addEventListener("click", () => {
    void sessions(el);
  });
}

// ------------------------------------------------------------------- detail

function cabecalho(nome: string, badges = ""): string {
  return `
    <header class="page-head">
      <div style="display:flex;align-items:center;gap:10px">
        <a class="sessions-back" href="#/sessoes" aria-label="Voltar para Sessions">←</a>
        <h1 class="page-title">${esc(nome)}</h1>${badges}
      </div>
      <div class="page-actions">${btnRefresh}</div>
    </header>`;
}

async function detalhe(el: HTMLElement, id: string): Promise<void> {
  el.innerHTML = cabecalho("Session") + skeleton();

  try {
    const { sessoes, agentes } = await dados();
    const s = sessoes.find((x) => x.id === id);

    if (!s) {
      el.innerHTML =
        cabecalho("Session") +
        empty(
          "Sessão não encontrada",
          "Ela pode ter saído das 50 mais recentes. Volte à lista e recarregue.",
          `<a class="btn" href="#/sessoes">Ver todas</a>`,
        );
    } else {
      const done = Boolean(s.ended_at);
      // DERIVADO do próprio registro: fim presente = Done, ausente = Running.
      const badge = done ? pill("Done", "idle") : pill("Running", "run");

      // Task é issue do GitHub (Contrato) e o título é dela; mas a sessão só
      // guarda o uuid da nossa tabela `tasks`, sem número de issue para casar.
      // Mostrar o id cru evita um segundo conjunto de títulos divergindo do
      // board — o vínculo sessão→issue é pergunta na issue #8.
      // Uma chave, uma chamada filtrada. Sem chave etiquetada não há de onde
      // tirar o número — some da meta em vez de virar zero.
      const total = s.litellm_key ? await tokensDaChave(s.litellm_key) : null;
      const tks = total ? `${kTokens(total)} tokens` : null;
      const meta = [
        `Task: ${esc(s.task_id)}`,
        done ? esc(duration(s.started_at, s.ended_at)) : "In progress",
        ...(done && tks ? [esc(tks)] : []),
        `Started ${esc(quando(s.started_at))}`,
      ]
        .map((m) => `<span>${m}</span>`)
        .join("");

      // Chip "Live" = mesmo estado do badge Running; os contadores de
      // mensagens/tool calls/files são derivados do transcript, que não chega
      // ao navegador (issue #8) — ficam fora em vez de mostrar zero mentiroso.
      const chips = done ? "" : `<div class="sessions-chips">${pill("Live", "ok")}</div>`;

      const log = empty(
        "Log indisponível",
        "O transcript é gravado pelo runner local e nenhum endpoint o serve ao navegador ainda (issue #8). Mensagens, tool calls e Files touched aparecem quando esse transporte existir.",
      );

      // ponytail: o botão "Open SDK" (Done) e o campo "Type a message…" (live)
      // ficam fora. O Contrato marca os dois como INFERIDO — runtime: retomar
      // ou falar com a sessão exige o Claude Code vivo na máquina do runner, e
      // não há destino nem endpoint (issue #8). A faixa abaixo é o que resta:
      // ela é derivada, não finge executar nada.
      const rodape = done
        ? `<div class="sessions-ended">Session ended — open in SDK to start a new one.</div>`
        : "";

      el.innerHTML =
        cabecalho(nomeAgente(agentes, s.role), badge) +
        `<div class="sessions-meta">${meta}</div>` +
        chips +
        log +
        rodape;
    }
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML =
      cabecalho("Session") +
      errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }

  ligaRefresh(el);
}

// -------------------------------------------------------------------- lista

export const sessions = async (el: HTMLElement): Promise<void> => {
  const id = location.hash.split("/")[2];
  if (id) return detalhe(el, decodeURIComponent(id));

  const header = head({ title: "Sessions", actions: btnRefresh });
  el.innerHTML = header + skeleton();

  try {
    const { sessoes, agentes } = await dados();

    // A coluna "Tokens" saiu da lista. O número é por chave etiquetada e só o
    // log filtrado por `api_key` o entrega — o que aqui significaria uma
    // chamada por linha (até 50) para preencher uma coluna. Trocar um download
    // de 81 MB por 50 requisições é o mesmo problema com outra roupa. O número
    // continua exato no detail, onde é uma chave e uma chamada.
    // O resto segue igual: nome do agente vem do LiteLLM, e a coluna Task
    // mostra o uuid enquanto não houver número de issue na sessão (issue #8).
    const linhas = sessoes.map(
      (s) => `
        <tr>
          <td><a href="#/sessoes/${esc(s.id)}"><strong>${esc(nomeAgente(agentes, s.role))}</strong></a></td>
          <td class="mono">${esc(s.task_id)}</td>
          <td>${s.ended_at ? pill("Done", "idle") : pill("Running", "run")}</td>
          <td>${esc(quando(s.started_at))}</td>
          <td>${s.ended_at ? esc(duration(s.started_at, s.ended_at)) : "In progress"}</td>
        </tr>`,
    );

    el.innerHTML =
      header +
      (linhas.length
        ? table(["Agent", "Task", "Status", "Started", "Duration"], linhas)
        : empty(
            "Nenhuma sessão",
            "Sessões nascem de agentes trabalhando em tasks; quando um runner registrar uma, ela aparece aqui.",
          ));
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML =
      header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }

  ligaRefresh(el);
};

// ------------------------------------------------------------------ checagem
//
// As duas únicas lógicas do arquivo: papel → nome canônico de agente e a soma
// de tokens por chave etiquetada. A menor checagem que falha se elas quebrarem.
if (import.meta.env.DEV) {
  const frota = [
    { agent_id: "a1", agent_name: "senior_dev", agent_card_params: { name: "Senior Dev" } },
  ] as Agent[];
  console.assert(nomeAgente(frota, "Senior Dev") === "Senior Dev", "nomeAgente: casa pelo card");
  console.assert(nomeAgente(frota, "senior dev") === "Senior Dev", "nomeAgente: normaliza espaço");
  console.assert(nomeAgente(frota, "revisor") === "revisor", "nomeAgente: sem casar, cai no papel");

  const log = [{ total_tokens: 8000 }, { total_tokens: 900 }, {}] as SpendLog[];
  console.assert(somaTokens(log) === 8900, "somaTokens: soma o log da chave, linha sem token vale 0");
  console.assert(kTokens(somaTokens(log)) === "8.9K", "kTokens: formato do spec");
  console.assert(somaTokens([]) === 0, "somaTokens: chave sem gasto some, não vira zero na tela");
}
